import { AsyncThunk, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { UserModel, UserRole } from "../../dto/auth/User";
import { loginUserEmail, logout, refreshTokens } from "../../api/requests/auth";
import { LoginEmailRequest } from "../../api/requests/interfaces/LoginEmailRequest";
import { AppDispatch, RootState } from "../store";
import { addNotification, NotificationType } from "./notificationsSlice";
import { jwtDecode } from "jwt-decode";
import { LocalStorageKeys } from "../../constants/localStorageKeys";
import { AccessToken } from "../interfaces/accessToken";
import { differenceInSeconds, isAfter } from "date-fns";
import { RefreshTokenRequest } from "../../api/requests/interfaces/RefreshTokenRequest";
import router from "../../routes/browserRouter";
import { SmartIdLoginRequest } from "../../api/requests/interfaces/SmartIDLoginRequest";

export interface AuthState {
    user?: UserModel;
    loading: boolean;
    error: boolean;
    initialized: boolean;
    apiAuthInitilized: boolean;
}

const initialState: AuthState = {
    user: undefined,
    loading: true,
    error: false,
    initialized: false,
    apiAuthInitilized: false
}

let intervalId: any;

export const tokenRefreshInterval = createAsyncThunk(
    'auth/tokenRefreshInterval',
    async (_, { dispatch, getState }) => {
        // Clear any existing interval first
        if (intervalId) clearInterval(intervalId);

        intervalId = setInterval(async () => {
            const state = getState() as RootState;
            const decodedToken = jwtDecode<AccessToken>(state.auth.user!.accessToken);
            if (differenceInSeconds(new Date(decodedToken.exp * 1000), new Date()) <= 60) {
                dispatch(createRefreshTokenRequest({accessToken: state.auth.user!.accessToken}))
                clearInterval(intervalId);
            }
        }, 5 * 1000); // Run every 5 seconds
    }
);

export const clearRefreshTokenInterval = () => {
    if (intervalId) clearInterval(intervalId);
}

export const createEmailLoginServiceRequest = createAsyncThunk(
    'auth/createEmailLoginServiceRequest',
    async (data: LoginEmailRequest, { rejectWithValue, dispatch }) => {
        try {
            return await loginUserEmail(data, dispatch as AppDispatch);
        } catch (e) {
            dispatch(addNotification({message: "Sisestatud andmed on valed või kontot pole loodud", type: NotificationType.ERROR}))
            rejectWithValue(e);
        }
    }
)

export const clearUserFromLocalStorage = createAsyncThunk(
    'auth/createEmailLoginServiceRequest',
    async (date: any, { rejectWithValue, dispatch }) => {
        localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
        localStorage.removeItem(LocalStorageKeys.USER_ROLE);
        localStorage.removeItem(LocalStorageKeys.USER_ID);
    }
)

export const createRefreshTokenRequest = createAsyncThunk(
    'auth/createRefreshTokenRequest',
    async (data: RefreshTokenRequest, { rejectWithValue, dispatch }) => {
        try {
            const refreshedToken = await refreshTokens(data, dispatch as AppDispatch);
            dispatch(setLoadingFinished());
            const newToken = jwtDecode<AccessToken>(refreshedToken.accessToken);
            dispatch(setUserState({
                role: newToken.role,
                accessToken: refreshedToken.accessToken,
                id: Number(newToken.sub)
            }));
            localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, refreshedToken.accessToken);
            localStorage.setItem(LocalStorageKeys.USER_ROLE, newToken.role);
            localStorage.setItem(LocalStorageKeys.USER_ID, newToken.sub);
            dispatch(tokenRefreshInterval());
        } catch (e) {
            dispatch(setLoadingFinished());
            dispatch(setUserState(undefined));
            dispatch(clearUserFromLocalStorage(""));
            dispatch(addNotification({message: "Sessioon aegus või taasautentimises esines viga, palun logige uuesti sisse", type: NotificationType.ERROR}));
            router.navigate('/login');
            rejectWithValue(e);
        }
    }
)

export const logUserOut = createAsyncThunk(
    'auth/logout',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const res = await logout();
            dispatch(setLoadingFinished());
            dispatch(setUserState(undefined));
            dispatch(clearUserFromLocalStorage(""));
            dispatch(addNotification({message: "Edukalt välja logitud", type: NotificationType.SUCCESS}));
            clearRefreshTokenInterval();
            router.navigate('/login');
        } catch {
            dispatch(addNotification({message: "Välja logimises esines viga", type: NotificationType.ERROR}));
        }
    }
)

export const createSmartIDLogin = createAsyncThunk(
    'auth/createSmartIDLogin',
    async (data: SmartIdLoginRequest, { dispatch }) => {
        try {
            const res = 
            router.navigate('/login');
        } catch (e) {
            dispatch(addNotification({message: "Smart-ID autentimine ebaõnnestus", type: NotificationType.ERROR}));
        }
    }
)

export const initAuthStateFromLocalStorage = createAsyncThunk(
    'auth/initAuthStateFromLocalStorage',
    async (data: void, { rejectWithValue, dispatch, getState }) => {
        const appState: RootState = getState() as any;
        if (appState.auth.initialized) {
            // if intervalid is not undefined, there was already app launched and first refresh token requesst made so we shoudln't make new request here and insted return
            return;
        }
        const accessToken = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
        const userRole = localStorage.getItem(LocalStorageKeys.USER_ROLE);
        const userId = localStorage.getItem(LocalStorageKeys.USER_ID);
        if (accessToken) {
            dispatch(setUserState({
                role: userRole ? userRole as UserRole : UserRole.USER,
                accessToken: accessToken,
                id: Number(userId)
            }))
            const decodedToken = jwtDecode<AccessToken>(accessToken);

            // if localstorage token is not expired yet, use this as logged in session
            if (isAfter(new Date(decodedToken.exp * 1000), new Date())) {
                dispatch(setUserState({
                    role: decodedToken.role,
                    accessToken: accessToken,
                    id: Number(userId)
                }))
                dispatch(setLoadingFinished());
                dispatch(tokenRefreshInterval());
            } else {
                // try to refresh token
                dispatch(setUserState({
                    role: decodedToken.role,
                    accessToken: accessToken,
                    id: Number(userId)
                }));
                dispatch(createRefreshTokenRequest({accessToken: accessToken}));
            }
            dispatch(setInitializedAuth())
        } else {
            dispatch(setLoadingFinished());
            dispatch(setUserState(undefined));
            dispatch(clearUserFromLocalStorage(""));
            // dispatch(createRefreshTokenRequest({accessToken: ""}))
        }
    }
)

type GenericAsyncThunk = AsyncThunk<unknown, unknown, any>;

type PendingAction = ReturnType<GenericAsyncThunk["pending"]>;
type RejectedAction = ReturnType<GenericAsyncThunk["rejected"]>;
type FulfilledAction = ReturnType<GenericAsyncThunk["fulfilled"]>;

export const authSlicce = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setUserState: (state, action: PayloadAction<UserModel | undefined>) => {
            state.user = action.payload
        },
        setInitializedAuth: (state) => {
            state.initialized = true
        },
        setLoadingFinished: (state) => {
            state.loading = false;
        },
        setApiauthInitialized: (state) => {
            state.apiAuthInitilized = true;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(createEmailLoginServiceRequest.pending, (state, action) => {
                state.loading = true;
                state.error = false;
            })
            .addCase(createEmailLoginServiceRequest.fulfilled, (state, action) => {
                if (action.payload) {
                    const decodedToken = jwtDecode<AccessToken>(action.payload.accessToken);
                    state.user = { accessToken: action.payload.accessToken, role: UserRole.USER, name: "Test user", id: Number(decodedToken.sub) }
                }
                state.loading = false;
                state.error = false;
            })
            .addCase(createEmailLoginServiceRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = false;
            })
    }
})

export const {setUserState, setInitializedAuth, setLoadingFinished, setApiauthInitialized} = authSlicce.actions;

export default authSlicce.reducer